import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
} from "@mui/material";
import {
  Business as ProjectIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as CompleteIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { ContractorLayout } from "../../components/layout/ContractorLayout";
import { supabase } from "../../lib/supabase";
import { ProjectDialog } from "./ProjectDialog";
import { Project, ProjectFormData } from "./projectTypes";
import { ProjectList } from "./ProjectList";
import { UpgradeModal } from "../../components/UpgradeModal";
import { useSubscription } from "../../hooks/useSubscription";
import { FREE_PROJECT_LIMIT, useProjects } from "./useProjects";

export function ContractorDashboard() {
  const { profile } = useAuth();
  const {
    projects,
    clientsById,
    loading,
    saving,
    listError,
    saveError,
    activeProjectCount,
    saveProject,
    deleteProject,
    clearSaveError,
    clearListError,
  } = useProjects(profile?.id);

  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "info" } | null>(null);
  const { isActive } = useSubscription();

  const [formData, setFormData] = useState<ProjectFormData>({
    title: "",
    address: "",
    clientEmail: "",
    status: "planning",
    startDate: "",
    estimatedCompletionDate: "",
    budget: "",
  });

  // Refresh pending approvals count whenever the projects list changes
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from("approval_requests")
      .select("*", { count: "exact", head: true })
      .eq("contractor_id", profile.id)
      .eq("status", "pending")
      .then(({ count, error }) => {
        if (!error && count !== null) setPendingApprovalsCount(count);
      });
  }, [profile?.id, projects]);

  const stats = useMemo(
    () => [
      {
        label: "Active Projects",
        value: activeProjectCount,
        icon: <ProjectIcon />,
        color: "#2563eb",
        bgColor: "#dbeafe",
      },
      {
        label: "Pending Approvals",
        value: pendingApprovalsCount,
        icon: <PendingIcon />,
        color: "#f59e0b",
        bgColor: "#fef3c7",
      },
      {
        label: "Updates This Week",
        value: 0,
        icon: <CompleteIcon />,
        color: "#10b981",
        bgColor: "#d1fae5",
      },
    ],
    [activeProjectCount, pendingApprovalsCount],
  );

  const resetForm = () => {
    setFormData({
      title: "",
      address: "",
      clientEmail: "",
      status: "planning",
      startDate: "",
      estimatedCompletionDate: "",
      budget: "",
    });
  };

  const openCreateDialog = () => {
    if (!isActive && activeProjectCount >= FREE_PROJECT_LIMIT) {
      setUpgradeOpen(true);
      return;
    }
    setEditingProject(null);
    resetForm();
    clearSaveError();
    setDialogOpen(true);
  };

  const openEditDialog = (project: Project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      address: project.address ?? "",
      clientEmail:
        clientsById[project.client_id ?? ""]?.email ??
        project.client_email_snap ??
        "",
      status: project.status,
      startDate: project.start_date ?? "",
      estimatedCompletionDate: project.estimated_completion_date ?? "",
      budget: project.budget ? project.budget.toString() : "",
    });
    clearSaveError();
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    clearSaveError();
  };

  const handleSaveProject = async (data: ProjectFormData) => {
    const contractorName =
      profile?.business_name || profile?.name || "Your contractor";
    const result = await saveProject(data, editingProject?.id, contractorName);

    if (!result.success) return;

    setDialogOpen(false);
    const isUpdate = !!editingProject;
    setSnackbar(
      result.invited
        ? {
            message: `Invite sent to ${result.clientEmail} — they'll get an email to set up their account.`,
            severity: "success",
          }
        : isUpdate
        ? {
            message: `Project updated and linked to ${result.clientEmail}.`,
            severity: "info",
          }
        : {
            message: `Project created and linked to ${result.clientEmail}.`,
            severity: "info",
          },
    );
  };

  const handleDeleteProject = (project: Project) => {
    setDeleteTarget(project);
    clearListError();
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteProject(deleteTarget.id);
    handleDeleteClose();
  };

  return (
    <ContractorLayout>
      <Box sx={{ mb: 4 }}>
        <Card
          elevation={0}
          sx={{ mb: 3, bgcolor: "primary.main", color: "white" }}
        >
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "white",
                  color: "primary.main",
                }}
              >
                {profile?.business_name?.charAt(0) ||
                  profile?.name?.charAt(0) ||
                  "C"}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Welcome back,{" "}
                  {profile?.business_name || profile?.name || "Contractor"}!
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                  Manage your construction projects and keep clients updated.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {stat.label}
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: stat.bgColor,
                        color: stat.color,
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Free tier usage indicator */}
        {!isActive && (
          <Box
            sx={{
              mb: 2,
              px: 2,
              py: 1.5,
              bgcolor: "grey.100",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Free plan:{" "}
              <strong>
                {activeProjectCount} / {FREE_PROJECT_LIMIT}
              </strong>{" "}
              active projects used
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setUpgradeOpen(true)}
            >
              Upgrade to Pro
            </Button>
          </Box>
        )}

        <ProjectList
          projects={projects}
          clientsById={clientsById}
          loading={loading}
          error={listError}
          onCreate={openCreateDialog}
          onEdit={openEditDialog}
          onDelete={handleDeleteProject}
        />
      </Box>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <ProjectDialog
        open={dialogOpen}
        saving={saving}
        error={saveError}
        initialData={formData}
        onClose={handleDialogClose}
        onSave={handleSaveProject}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity ?? "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete project</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete {deleteTarget?.title}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </ContractorLayout>
  );
}
